import React from 'react';
import { useRef, useMemo } from 'react';
import { Box, Typography } from '@mui/material';

import {
  SectionContent,
  PageHeader,
  StyledList,
  GoBackButton,
  ImageTextOverlay
} from '../../components';
import { INTERVIEW_CONTENT } from '../../data';
import ROUTES from '../../config/routes';
import THEME from '../../constants/theme';
import {
  SECTION_SPACING,
  SUBSECTION_SPACING,
  ELEMENT_SPACING
} from '../../constants';
import mqcapitalImage25 from '../../assets/images/how-to-join/mqcapital-image-25.jpg';
import SEO from '../../components/SEO';
import { seoData } from '../../data/seoData';

const InterviewPage = ({ setRefs }) => {
  const overviewRef = useRef(null);
  const compellingRef = useRef(null);
  const processRef = useRef(null);
  const preparationRef = useRef(null);

  const refs = useMemo(
    () => ({
      overview: overviewRef,
      compelling: compellingRef,
      process: processRef,
      preparation: preparationRef
    }),
    []
  );

  // Update global refs if setRefs function is provided
  React.useEffect(() => {
    if (setRefs && typeof setRefs === 'function') {
      setRefs(refs);
    }
  }, [refs, setRefs]);

  return (
    <>
      <SEO
        title={seoData.interview.title}
        content={seoData.interview.content}
        keywords={seoData.interview.keywords}
        canonical={seoData.interview.canonical}
        ogImage={seoData.interview.ogImage}
      />
      {/* Back Button */}}
      <GoBackButton
        backTo={ROUTES.HOW_TO_JOIN.ROOT}
        backText='Back to How to Join'
      />
      {/* Main "Interview" section */}
      <PageHeader
        id='hero'
        title={INTERVIEW_CONTENT.TITLE}
        align='left'
        sx={{ mb: SECTION_SPACING }}
      />
      <ImageTextOverlay
        imageSrc={mqcapitalImage25}
        text={INTERVIEW_CONTENT.INTRO[0]}
        height='200px'
        boxSx={{ width: '100%', zIndex: 2 }}
        textSx={{
          fontSize: '1.3rem',
          lineHeight: 1.6,
          color: THEME.COLORS.NEUTRAL_WHITE
        }}
        containerSx={{
          position: 'relative',
          '&:before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            zIndex: 1
          }
        }}
      />
      {/* Render the second paragraph as normal SectionContent text */}
      <SectionContent
        id='overview'
        ref={refs.overview}
        initialSpacing={false}
        text={[
          {
            text: INTERVIEW_CONTENT.INTRO[1],
            marginBottom: SECTION_SPACING,
            marginTop: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />
      {/* What We Look for in Compelling Candidates */}
      <SectionContent
        id='compelling'
        ref={refs.compelling}
        title={INTERVIEW_CONTENT.COMPELLING_CANDIDATES.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: INTERVIEW_CONTENT.COMPELLING_CANDIDATES.INTRO,
            marginBottom: ELEMENT_SPACING * 2,
            fontSize: '1.3rem'
          }
        ]}
      />
      <Box
        sx={{
          width: '100%',
          marginBottom: SECTION_SPACING,
          paddingLeft: {
            xs: THEME.SPACING.MEDIUM,
            sm: THEME.SPACING.MEDIUM,
            md: THEME.SPACING.XL
          }
        }}
      >
        <StyledList items={INTERVIEW_CONTENT.COMPELLING_CANDIDATES.ITEMS} />
      </Box>
      {/* Our Interview Process */}
      <SectionContent
        id='process'
        ref={refs.process}
        title={INTERVIEW_CONTENT.PROCESS.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: INTERVIEW_CONTENT.PROCESS.INTRO,
            marginBottom: ELEMENT_SPACING * 2,
            fontSize: '1.3rem'
          }
        ]}
      />
      <Box
        sx={{
          width: '100%',
          marginBottom: SECTION_SPACING,
          paddingLeft: {
            xs: THEME.SPACING.MEDIUM,
            sm: THEME.SPACING.MEDIUM,
            md: THEME.SPACING.XL
          }
        }}
      >
        <StyledList items={INTERVIEW_CONTENT.PROCESS.STEPS} />
      </Box>
      {/* Preparing for the Interview */}
      <SectionContent
        id='preparation'
        ref={refs.preparation}
        title={INTERVIEW_CONTENT.PREPARATION.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        linkMappings={[
          {
            word: 'guide to preparing for an interview',
            url: '/how-to-join/interview/preparing-for-interview',
            color: THEME.COLORS.PRIMARY_BLUE,
            textDecoration: 'underlined',
            newTab: false
          }
        ]}
        text={INTERVIEW_CONTENT.PREPARATION.TEXT.map((text, index, array) => ({
          text,
          marginBottom:
            index === array.length - 1 ? SECTION_SPACING : ELEMENT_SPACING,
          fontSize: '1.3rem'
        }))}
      />
      {/* Footer note with separator line */}
      <Box
        sx={{
          borderTop: `1px solid ${THEME.COLORS.NEUTRAL_GRAY}`,
          paddingTop: ELEMENT_SPACING,
          paddingBottom: ELEMENT_SPACING,
          mt: 4,
          mb: 2
        }}
      >
        <Typography
          variant='body2'
          sx={{
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
            fontSize: '1rem',
            fontStyle: 'italic',
            color: THEME.COLORS.NEUTRAL_GRAY,
            lineHeight: 1.6,
            display: 'block'
          }}
        >
          {INTERVIEW_CONTENT.FOOTER_NOTE}
        </Typography>
      </Box>
    </>
  );
};

export default InterviewPage;
